import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { PaginatorModule } from 'primeng/paginator';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';

import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { EnumService } from '@shared/api/services/enum.service';
import {
  AtivityLogsService,
  AtivityLogItem,
  AtivityLogsFilter,
} from '@shared/api/services/ativity-logs.service';
import { DetalheRegistoDrawerComponent } from './detalhe-registo-drawer/detalhe-registo-drawer.component';
import { resolverVisualAcao } from './registos-acao-visual';

export interface EnumOptionItem {
  label: string;
  value: number;
}

interface FiltroChip {
  key: string;
  label: string;
}

const FILTRO_LABELS: Record<string, string> = {
  Nome: 'Nome',
  Email: 'E-mail',
  AcaoLogsEnum: 'Ação',
  EntidadeENUM: 'Entidade',
  DataHora: 'Data/Hora',
};

@Component({
  selector: 'app-registos',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    Select,
    DatePicker,
    PaginatorModule,
    SkeletonModule,
    TooltipModule,
    EmptyStateComponent,
    DetalheRegistoDrawerComponent,
  ],
  templateUrl: './registos.component.html',
  styleUrl: './registos.component.scss',
})
export class RegistosComponent implements OnInit {
  logs: AtivityLogItem[] = [];
  loading = false;
  totalRecords = 0;
  totalPages = 0;
  pageNumber = 1;
  pageSize = 10;

  filtrosPainelAberto = false;
  filtrosForm!: FormGroup;
  searchTerm = '';

  selectedLog: AtivityLogItem | null = null;
  mostrarDetalheDrawer = false;

  private lastFiltros: Record<string, any> = {};

  acaoOptions: EnumOptionItem[] = [
    { label: 'Criar', value: 1 },
    { label: 'Editar', value: 2 },
    { label: 'Eliminar', value: 3 },
    { label: 'Visualizar', value: 4 },
    { label: 'Login', value: 5 },
    { label: 'Logout', value: 6 },
  ];

  entidadeOptions: EnumOptionItem[] = [
    { label: 'Efetivo', value: 1 },
    { label: 'Posto', value: 2 },
    { label: 'Escala', value: 3 },
    { label: 'Equipamento', value: 4 },
    { label: 'Ocorrência', value: 5 },
    { label: 'Cliente', value: 6 },
    { label: 'Contrato Efetivo', value: 7 },
    { label: 'Contrato Cliente', value: 8 },
    { label: 'Utilizador', value: 9 },
    { label: 'Ronda', value: 10 },
  ];

  private readonly fb = inject(FormBuilder);
  private readonly ativityLogsSvc = inject(AtivityLogsService);
  private readonly enumSvc = inject(EnumService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.initForm();
    this.loadEnums();
    this.loadLogs();
  }

  private initForm(): void {
    this.filtrosForm = this.fb.group({
      Nome: [''],
      Email: [''],
      AcaoLogsEnum: [null],
      EntidadeENUM: [null],
      DataHora: [null],
    });
  }

  private loadEnums(): void {
    // Tenta obter as enums dinamicamente do backend se os endpoints existirem
    this.enumSvc
      .getEnum('AcaoLogs')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res && Object.keys(res).length > 0) {
            this.acaoOptions = Object.entries(res).map(([k, v]) => ({
              label: v as string,
              value: Number(k),
            }));
            this.cdr.markForCheck();
          }
        },
        error: () => {
          // Mantém as opções fallback pré-definidas se falhar
        },
      });

    this.enumSvc
      .getEnum('EntidadeLogs')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res && Object.keys(res).length > 0) {
            this.entidadeOptions = Object.entries(res).map(([k, v]) => ({
              label: v as string,
              value: Number(k),
            }));
            this.cdr.markForCheck();
          }
        },
        error: () => {
          // Mantém as opções fallback pré-definidas se falhar
        },
      });
  }

  loadLogs(extraParams: Record<string, any> = {}): void {
    this.loading = true;
    this.cdr.markForCheck();

    const filters: AtivityLogsFilter = {
      PageNumber: this.pageNumber,
      PageSize: this.pageSize,
      ...this.lastFiltros,
      ...extraParams,
    };

    // Se houver pesquisa rápida no topo
    if (this.searchTerm.trim()) {
      const term = this.searchTerm.trim();
      if (term.includes('@')) {
        filters['Email'] = term;
      } else {
        filters['Nome'] = term;
      }
    }

    const dataInicioVal = filters['DataInicio'] as any;
    if (dataInicioVal && (dataInicioVal instanceof Date || typeof dataInicioVal.toISOString === 'function')) {
      filters['DataInicio'] = dataInicioVal.toISOString();
    }
    const dataFimVal = filters['DataFim'] as any;
    if (dataFimVal && (dataFimVal instanceof Date || typeof dataFimVal.toISOString === 'function')) {
      filters['DataFim'] = dataFimVal.toISOString();
    }

    this.ativityLogsSvc
      .getAll(filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.logs = res.data ?? [];
          this.pageNumber = res.pageNumber ?? 1;
          this.pageSize = res.pageSize ?? 10;
          this.totalRecords = res.totalRecords ?? this.logs.length;
          this.totalPages =
            res.totalPages ?? Math.ceil(this.totalRecords / this.pageSize);
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Erro ao carregar registos de atividade:', err);
          this.logs = [];
          this.totalRecords = 0;
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  onSearch(term: string): void {
    this.searchTerm = term;
    this.pageNumber = 1;
    this.loadLogs();
  }

  aplicarFiltrosDrawer(): void {
    const val = this.filtrosForm.value;
    const params: Record<string, any> = {};

    Object.entries(val).forEach(([k, v]) => {
      if (k === 'DataHora') {
        if (v instanceof Date && !isNaN(v.getTime())) {
          const inicio = new Date(v);
          inicio.setHours(0, 0, 0, 0);
          const fim = new Date(v);
          fim.setHours(23, 59, 59, 999);
          params['DataInicio'] = inicio;
          params['DataFim'] = fim;
        }
        return;
      }
      if (v !== null && v !== undefined && v !== '') {
        params[k] = v;
      }
    });

    this.lastFiltros = params;
    this.filtrosPainelAberto = false;
    this.pageNumber = 1;
    this.loadLogs();
  }

  limparFiltros(): void {
    this.filtrosForm.reset();
    this.searchTerm = '';
    this.lastFiltros = {};
    this.pageNumber = 1;
    this.loadLogs();
  }

  limparFiltrosDrawer(): void {
    this.filtrosForm.reset();
  }

  removerFiltro(key: string): void {
    if (key === 'DataHora') {
      this.filtrosForm.get('DataHora')?.setValue(null);
      delete this.lastFiltros['DataInicio'];
      delete this.lastFiltros['DataFim'];
    } else {
      this.filtrosForm.get(key)?.setValue(null);
    }
    this.aplicarFiltrosDrawer();
  }

  onPageChange(event: any): void {
    this.pageNumber = (event.page ?? 0) + 1;
    this.pageSize = event.rows ?? this.pageSize;
    this.loadLogs();
  }

  verDetalhes(log: AtivityLogItem): void {
    this.selectedLog = log;
    this.mostrarDetalheDrawer = true;
  }

  get activeFilterCount(): number {
    return Object.values(this.filtrosForm?.value ?? {}).filter(
      (v) => v !== null && v !== undefined && v !== '',
    ).length;
  }

  get filtroChips(): FiltroChip[] {
    const chips: FiltroChip[] = Object.entries(this.lastFiltros)
      .filter(([key]) => key !== 'DataInicio' && key !== 'DataFim')
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([key, value]) => ({
        key,
        label: `${FILTRO_LABELS[key] ?? key}: ${this.formatFiltroValue(key, value)}`,
      }));

    // DataInicio/DataFim são internos: mostram-se num único chip "Data/Hora"
    const dataHora = this.lastFiltros['DataInicio'];
    if (dataHora) {
      chips.push({
        key: 'DataHora',
        label: `${FILTRO_LABELS['DataHora']}: ${this.formatFiltroValue('DataHora', dataHora)}`,
      });
    }

    return chips;
  }

  private formatFiltroValue(key: string, value: any): string {
    if (key === 'AcaoLogsEnum') {
      return (
        this.acaoOptions.find((o) => o.value === Number(value))?.label ?? value
      );
    }
    if (key === 'EntidadeENUM') {
      return (
        this.entidadeOptions.find((o) => o.value === Number(value))?.label ??
        value
      );
    }
    if (value instanceof Date) {
      return value.toLocaleDateString('pt-PT');
    }
    return String(value);
  }

  getAcaoClass(acaoEnum?: number, acaoLogsStr?: string): string {
    return resolverVisualAcao(acaoEnum, acaoLogsStr).badge;
  }

  getAcaoIcon(acaoEnum?: number, acaoLogsStr?: string): string {
    return resolverVisualAcao(acaoEnum, acaoLogsStr).icon;
  }

  trackByLogId(_index: number, item: AtivityLogItem): number {
    return item.id;
  }
}
